import classNames from 'classnames';
import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { FormattedMessage } from 'react-intl';

import { SITE_MODE, SITE_MODES } from 'config/site-mode';

import {
  Price,
  Switch,
  TableAlternate,
} from 'components';

import defaultThumb from 'img/logo/default-thumb.png';
import Button from 'components/button';
import { SHOW_PRICES } from 'config/shopping-cart';

export default class SystemsDetailQuote extends Component {

  /* Lifecycle */

  constructor(props) {
    super(props);

    this.SYSTEM_COLUMNS = [
      {
        accessor: 'name',
        cell: row => this.generateTableSwitch(row),
        header: props.intl.messages['systems.quote.columns.name'],
      }, {
        accessor: 'sap',
        cell: row => this.generateSystemCell(row, 'sap', false),
        header: props.intl.messages['systems.quote.columns.serial_number'],
        modifier: 'shrink',
      }, {
        accessor: 'amount',
        cell: row => this.generateEditableCell(row, 'amount'),
        header: props.intl.messages['systems.quote.columns.amount'],
        modifier: 'shrink',
      },
    ];

    if (SHOW_PRICES) {
      this.SYSTEM_COLUMNS = this.SYSTEM_COLUMNS.concat([{
        accessor: 'price',
        cell: row => this.generateSystemCell(row, 'price'),
        header: props.intl.messages['systems.quote.columns.price'],
        modifier: 'shrink',
      }, {
        accessor: 'vat_percentage',
        cell: row => this.generateSystemCell(row, 'vat_percentage', false),
        header: props.intl.messages['systems.quote.columns.vat_percentage'],
        modifier: 'shrink',
      }, {
        accessor: 'vat_total',
        cell: row => this.generateSystemCell(row, 'vat_total'),
        header: props.intl.messages['systems.quote.columns.vat_total'],
        modifier: 'shrink',
      },
      {
        accessor: 'subtotal',
        cell: row => this.generateSystemCell(row, 'subtotal'),
        header: props.intl.messages['systems.quote.columns.subtotal'],
        modifier: 'shrink',
      }]);
    }


    this.state = {
      projectId: props.params.projectId,
      tableSystem: [],
    };
  }

  componentDidMount() {
    const { params, products, projectGet, projectGetProducts } = this.props;
    const { projectId } = this.state;

    if (products.data) {
      this.formatProductsData(products);
    }

    projectGet(params.projectId);
    projectGetProducts(projectId);
  }

  componentWillReceiveProps(nextProps) {
    if (nextProps.products.data) {
      const oldProducts = JSON.stringify(this.props.products.data);
      const newProducts = JSON.stringify(nextProps.products.data);

      if (newProducts !== oldProducts) {
        this.formatProductsData(nextProps.products);
      }
    }
  }

  /* Handlers */

  tableCellBlurHandler(event, row, type, isPrice) {
    const { products, projectUpdateProduct } = this.props;
    const { projectId } = this.state;
    let value = event.target.innerHTML;

    if (this.validateNumber(value, row.slot, row.id, type)) {
      value = Number(event.target.innerHTML);
      value = isPrice ? value : Math.round(value);

      const data = {
        attributes: {
          slot: row.slot,
          [type]: value,
          project_id: projectId,
        },
        relationships: {
          project: {
            data: {
              type: 'Project',
              id: projectId,
            },
          },
        },
      };

      projectUpdateProduct(row.id, { data }).then(() => {
        this.formatProductsData(products);
      }).catch(() => {
        this.invalidateRow(true, type, row.id);
      });
    }
  }

  tableCellKeyPressHandler(event) {
    if (event.key === 'Enter') {
      event.preventDefault();
      event.target.blur();
    }
  }

  tableSwitchHandler(value, row) {
    const { products, projectUpdateProduct } = this.props;
    const { projectId } = this.state;

    const data = {
      attributes: {
        slot: row.slot,
        exclude_price: !value,
        project_id: projectId,
      },
      relationships: {
        project: {
          data: {
            type: 'Project',
            id: projectId,
          },
        },
      },
    };

    projectUpdateProduct(row.id, { data }).then(() => {
      this.formatProductsData(products);
    });
  }

  /* Methods */

  calculateTableSubtotal(table) {
    let result = 0;

    if (table.length) {
      table.forEach((row) => {
        if (!row.exclude_price) {
          result += row.subtotal;
        }
      });
    }

    result = Math.round(result * 100) / 100;

    return result;
  }

  calculateTotal() {
    const { products: { data } } = this.props;
    const { toggleDiscounts, togglePersonalCosts } = this.state;

    let subtotalInclVAT = 0;
    let totalDiscount = 0;
    let totalVAT = 0;

    if (data) {
      data.forEach((item) => {
        const product = item.attributes;

        if (!product.exclude_price) {
          if (product.slot === 'discounts' && toggleDiscounts) {
            totalDiscount += product.subtotal;
          } else if ((product.slot === 'personal-costs' && togglePersonalCosts) || (product.slot !== 'personal-costs' && product.slot !== 'discounts')) {
            totalVAT += product.vat_total;
            subtotalInclVAT += product.subtotal;
          }
        }
      });
    }

    return {
      subtotalExclVAT: subtotalInclVAT - totalVAT,
      subtotalInclVAT,
      totalAll: subtotalInclVAT - totalDiscount, // Discount is a subtraction of subtotal (incl VAT)
      totalDiscount,
      totalVAT,
    };
  }

  formatProductsData(products) {
    const tableSystem = [];

    products.data.forEach((product) => {
      const newProduct = {
        ...product.attributes,
        id: product.id,
        checked: false,
      };

      if (product.attributes.slot !== 'personal-costs' && product.attributes.slot !== 'discounts') {
        tableSystem.push(newProduct);
      }
    });

    this.setState({
      tableSystem,
    });
  }

  generateEditableCell(row, cell, isPrice = false) {
    const editableCellClassNames = classNames(
      'table-alternate-body__content',
      'table-alternate-body__content--editable',
      {
        'is-invalid': row[`${cell}_invalid`],
        'is-inactive': row.exclude_price,
        'table-alternate-body__content--has-price': isPrice,
        'table-alternate-body__content--align-right': isPrice,
        'u-text-center': !isPrice,
      },
    );

    const value = Number(row[cell]);
    const content = isPrice ? value.toFixed(2) : value;

    if (row.exclude_price) {
      return (<div className={editableCellClassNames}>{content}</div>);
    }

    return (
      <div
        className={editableCellClassNames}
        contentEditable
        onBlur={e => this.tableCellBlurHandler(e, row, cell, isPrice)}
        onKeyPress={this.tableCellKeyPressHandler}
        dangerouslySetInnerHTML={{ __html: content }} // eslint-disable-line
        role="textbox"
      />
    );
  }

  generateSystemCell(row, cell, isPrice = true) {
    const cellClassnames = classNames(
      'table-alternate-body__content',
      {
        'is-inactive': row.exclude_price,
        'table-alternate-body__content--align-right': isPrice,
      },
    );

    const content = isPrice ? <Price value={row[cell]} /> : row[cell];

    return (
      <div className={cellClassnames}>{content}</div>
    );
  }

  generateTableSwitch(row) {
    let content = row.name;
    const cellClassnames = classNames(
      'table-alternate-body__content',
      { 'is-inactive': row.exclude_price },
    );

    if (row.parent_id) {
      content = (
        <Switch
          disabled={row.required}
          label={row.name}
          onChange={value => this.tableSwitchHandler(value, row)}
          value={!row.exclude_price}
        />
      );
    }

    return (
      <div className={cellClassnames}>
        {content}
      </div>
    );
  }

  invalidateRow(invalid, type, id) {
    // Update row invalid state for css classname
    const tableName = 'tableSystem';

    const table = this.state[tableName];
    const index = table.findIndex((item) => { return (item.id === id); });
    const name = `${type}_invalid`;
    table[index][name] = invalid;

    this.setState({ [tableName]: table });
  }

  validateNumber(number, slot, id, type) {
    const invalid = isNaN(Number(number));

    this.invalidateRow(invalid, type, id);

    return !invalid;
  }

  /* Render */

  render() {
    return (
      <div className="view__content">
        <div className="container">
          <div className="row">
            <div className="gr-12">
              <div className="page quote">
                {this.renderSystemInfo()}
                {this.renderSystemTable()}
                {SHOW_PRICES && this.renderCalculationTotal()}
                {SHOW_PRICES && this.renderSystemFooter()}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  renderShoppingCartButton() {
    const { params, shoppingCartError, shoppingCartSendProducts, shoppingCartIsLoading } = this.props;
    const { projectId } = params;

    const isRensa = SITE_MODE === SITE_MODES.RENSA;
    const buttonColorClass = isRensa ? 'button-bg-rensa button-color-white' : 'button-bg-yellow';
    const buttonTranslationKey = isRensa ? 'shoppingCart.addRensa' : 'shoppingCart.add';

    return (
      <div className="align-right">
        <Button
          className={`button ${buttonColorClass} button-regular`}
          disabled={shoppingCartIsLoading}
          loading={shoppingCartIsLoading}
          onClick={() => shoppingCartSendProducts(projectId)}
        >
          <FormattedMessage id={buttonTranslationKey} />
        </Button>
        {shoppingCartError &&
          <p className="u-color-state-danger u-margin-top-sm">
            <FormattedMessage id="shoppingCart.error" />
          </p>
        }
      </div>
    );
  }

  renderSystemInfo() {
    const { project, shoppingCartToken } = this.props;

    if (!project || !project.data || !project.data.attributes) {
      return null;
    }

    return (
      <div className="section">
        <div className="section__container">
          <div className="section__content">
            <div className="align align--align-top align--fluid align--gutter-lg">
              {this.renderSystemSketch()}
              <div className="align__cell">
                <div className="section__title section__title--gutter-md">{project.data.attributes.name}</div>
              </div>
              {((shoppingCartToken && SITE_MODE === SITE_MODES.RENSA) || SITE_MODE === SITE_MODES.TU) &&
                <div className="align__cell">
                  {this.renderShoppingCartButton()}
                </div>
              }
            </div>
            <div className="section__paragraph section__paragraph--max-width">
              <FormattedMessage id="systems.quote.info" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  renderSystemFooter() {
    return (
      <div className="section">
        <div className="section__container">
          <div className="section__content">
            <div className="section__paragraph section__paragraph--max-width">
              <FormattedMessage id="systems.quote.footer" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  renderSystemTable() {
    const { products } = this.props;
    const { tableSystem } = this.state;
    const subtotal = this.calculateTableSubtotal(tableSystem);
    const loading = products.get.loading || products.post.loading || products.put.loading;

    return (
      <div className="section">
        <div className="section__container">
          <div className="section__content">
            <TableAlternate
              columns={this.SYSTEM_COLUMNS}
              loading={loading}
              rows={tableSystem}
              subtotal={subtotal}
              showPrices={SHOW_PRICES}
              includesVat
            />
          </div>
        </div>
      </div>
    );
  }

  renderSystemSketch() {
    const { project } = this.props;

    if (!project || !project.data || !project.data.attributes || (!project.data.attributes.schema && !project.data.attributes.sketch)) {
      return null;
    }

    return (
      <div className="align__cell align__cell--shrink">
        <div className="offset">
          <img
            alt="Schema"
            className="quote__sketch"
            src={project.data.attributes.schema || project.data.attributes.sketch || defaultThumb}
            title={project.data.attributes.name}
          />
        </div>
      </div>
    );
  }

  renderCalculationTotal() {
    const calculation = this.calculateTotal();

    return (
      <div className="section">
        <div className="section__container">
          <div className="align align--fluid">
            <div className="align__cell align__cell" />
            <div className="align__cell align__cell--shrink">
              <div className="quote__calculation">
                <div className="align align--fluid align--gutter-sm align--v-gutter-sm">
                  <div className="align__row">
                    <div className="align__cell">
                      <FormattedMessage id="systems.quote.calculation.subtotalExclVAT" />
                    </div>
                    <div className="align__cell u-text-right">
                      <Price value={calculation.subtotalExclVAT} />
                    </div>
                  </div>
                  <div className="align__row">
                    <div className="align__cell">
                      <FormattedMessage id="systems.quote.calculation.vat" />
                    </div>
                    <div className="align__cell u-text-right">
                      <Price value={calculation.totalVAT} />
                    </div>
                  </div>
                  <div className="align__row">
                    <div className="align__cell">
                      <FormattedMessage id="systems.quote.calculation.subtotalInclVAT" />
                    </div>
                    <div className="align__cell u-text-right">
                      <Price value={calculation.subtotalInclVAT} />
                    </div>
                  </div>
                  <div className="align__row quote__total">
                    <div className="align__cell">
                      <FormattedMessage id="systems.quote.calculation.total" />
                    </div>
                    <div className="align__cell u-text-right">
                      <Price value={calculation.totalAll} />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
}

SystemsDetailQuote.propTypes = {
  intl: PropTypes.object,
  params: PropTypes.object,
  products: PropTypes.object,
  project: PropTypes.object,
  projectGet: PropTypes.func,
  projectGetProducts: PropTypes.func,
  projectUpdateProduct: PropTypes.func,
  shoppingCartError: PropTypes.string,
  shoppingCartIsLoading: PropTypes.bool,
  shoppingCartSendProducts: PropTypes.func,
  shoppingCartToken: PropTypes.string,
};



// WEBPACK FOOTER //
// ./js/views/systems/detail/quote/index.jsx